// Audit Log Storage
// Central store for all system audit events — settings changes, project edits,
// resource changes, budget updates, and anything else that should be tracked.
//
// Live mode: calls the backend API (GET /api/audit-log)
// Demo mode: uses localStorage-backed data
//
// AuditEntry.category uses a fixed set of values for filtering in the report UI.
// AuditEntry.action describes what happened (e.g. "Updated", "Created", "Deleted").
// AuditEntry.entityName identifies what was affected (e.g. "Budget Health Thresholds").
// AuditEntry.oldValue / newValue are human-readable strings (not raw JSON).

import { getApiBaseUrl, getDataMode } from './data-service';
import { authFetch } from './auth-fetch';

export type AuditCategory = 'Settings' | 'Project' | 'Resource' | 'Budget' | 'Client';

export interface AuditEntry {
  /** Unique entry ID (UUID v4) */
  id: string;
  /** ISO timestamp of when the change occurred */
  timestamp: string;
  /** Who made the change — "Demo User" in demo mode, user display name in live mode */
  changedBy: string;
  /** Broad category for filtering */
  category: AuditCategory;
  /** Short verb describing the action, e.g. "Updated", "Created", "Deleted" */
  action: string;
  /** Name of the thing that was changed, e.g. "Budget Health Thresholds" */
  entityName: string;
  /** Human-readable previous value (optional) */
  oldValue?: string;
  /** Human-readable new value (optional) */
  newValue?: string;
  /** Extra detail / free-text context (optional) */
  detail?: string;
  /** Company ID for multi-tenant scoping */
  companyId?: string | number;
}

const STORAGE_KEY = 'system-audit-log';
const MAX_ENTRIES = 1000; // cap to avoid unbounded localStorage growth

/** Generate a simple UUID v4 */
function generateId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Load all audit entries from localStorage, newest first.
 */
export function getAuditLog(): AuditEntry[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as AuditEntry[];
    }
  } catch (error) {
    console.error('Error loading audit log:', error);
  }
  return [];
}

/**
 * Append a new entry to the audit log.
 * Entries are stored newest-first. Automatically caps at MAX_ENTRIES.
 */
export function appendAuditEntry(
  entry: Omit<AuditEntry, 'id' | 'timestamp'>
): AuditEntry {
  const newEntry: AuditEntry = {
    ...entry,
    id: generateId(),
    timestamp: new Date().toISOString(),
  };

  try {
    const existing = getAuditLog();
    const updated = [newEntry, ...existing].slice(0, MAX_ENTRIES);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  } catch (error) {
    console.error('Error writing audit log:', error);
  }

  return newEntry;
}

/**
 * Clear all audit log entries (admin utility — use with caution).
 */
export function clearAuditLog(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing audit log:', error);
  }
}

// ─── API-backed audit log (live mode) ─────────────────────────────────────────

export interface AuditLogApiParams {
  category?: string;
  from?: string;
  to?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface AuditLogApiResponse {
  entries: AuditEntry[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Fetch audit log entries from the backend API.
 * Maps the backend response shape (changedAt, oldValue, newValue, detail)
 * to the frontend AuditEntry shape.
 */
export async function fetchAuditLogFromApi(
  params: AuditLogApiParams = {}
): Promise<AuditLogApiResponse> {
  const qs = new URLSearchParams();
  if (params.category) qs.append('category', params.category);
  if (params.from) qs.append('from', params.from);
  if (params.to) qs.append('to', params.to);
  if (params.search) qs.append('search', params.search);
  qs.append('page', String(params.page ?? 1));
  qs.append('pageSize', String(params.pageSize ?? 50));

  const url = `${getApiBaseUrl()}/audit-log?${qs.toString()}`;
  const response = await authFetch(url);

  if (!response.ok) {
    throw new Error(`Failed to fetch audit log: ${response.status}`);
  }

  const data = await response.json();

  // Map backend fields to frontend AuditEntry shape
  const entries: AuditEntry[] = (data.entries ?? []).map((e: any) => ({
    id: String(e.id),
    timestamp: e.changedAt,
    changedBy: e.changedBy ?? 'System',
    category: e.category as AuditCategory,
    action: e.action ?? '',
    entityName: e.entityName ?? '',
    oldValue: e.oldValue ?? undefined,
    newValue: e.newValue ?? undefined,
    detail: e.detail ?? undefined,
    companyId: e.companyId ?? undefined,
  }));

  return {
    entries,
    total: data.total ?? entries.length,
    page: data.page ?? 1,
    pageSize: data.pageSize ?? 50,
  };
}

/**
 * Returns true when the app should use the backend API for audit data.
 */
export function shouldUseLiveAuditApi(): boolean {
  return getDataMode() === 'live';
}
